import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon
  const [sessionTimeout, setSessionTimeout] = useState(60);
  const [impersonation, setImpersonation] = useState(() => {
    const raw = localStorage.getItem("crm_impersonation");
    return raw ? JSON.parse(raw) : null;
  });

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("crm_token");
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      const cached = localStorage.getItem("crm_session_timeout");
      if (cached) setSessionTimeout(parseInt(cached, 10) || 60);
    } catch {
      localStorage.removeItem("crm_token");
      localStorage.removeItem("crm_user");
      setUser(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    localStorage.setItem("crm_token", data.token);
    localStorage.setItem("crm_user", JSON.stringify(data.user));
    localStorage.setItem("crm_session_timeout", String(data.session_timeout_minutes));
    setUser(data.user);
    setSessionTimeout(data.session_timeout_minutes);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("crm_token");
    localStorage.removeItem("crm_user");
    localStorage.removeItem("crm_impersonation");
    localStorage.removeItem("crm_master_token");
    setImpersonation(null);
    setUser(false);
    window.location.href = "/login";
  };

  const startImpersonate = async (userId) => {
    const { data } = await api.post(`/users/${userId}/impersonate`);
    // Save the master admin's existing token
    const masterToken = localStorage.getItem("crm_token");
    localStorage.setItem("crm_master_token", masterToken);
    localStorage.setItem("crm_token", data.token);
    localStorage.setItem("crm_impersonation", JSON.stringify({
      target_id: data.user.id,
      target_name: data.user.full_name,
      master_id: data.impersonator.id,
    }));
    setImpersonation({
      target_id: data.user.id,
      target_name: data.user.full_name,
      master_id: data.impersonator.id,
    });
    setUser(data.user);
    window.location.href = "/dashboard";
  };

  const stopImpersonate = () => {
    const masterToken = localStorage.getItem("crm_master_token");
    if (masterToken) {
      localStorage.setItem("crm_token", masterToken);
      localStorage.removeItem("crm_master_token");
    }
    localStorage.removeItem("crm_impersonation");
    setImpersonation(null);
    window.location.href = "/analytics";
  };

  return (
    <AuthContext.Provider value={{
      user, login, logout, sessionTimeout, reload: loadMe,
      impersonation, startImpersonate, stopImpersonate,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
